import { Component } from 'preact'
import { ParentCategoryInfo, WidgetSetting } from './Types'
import TrackingLink from '../link/tracking-link'
import { CurrentUserContext } from '../../server-middleware/userContext'
import Dropdown from '../User/Dropdown'
import classNames from 'classnames'
import Slider from '../slider'
import Icon from '../assets'
import Phrase from '../../libs/translationsUtils/phrase'
import { ModulesContext } from '../../utils/globals'
import MegaMenuLinks from './megaMenuLinks'
import MegaMenuSearch from './megaMenuSearch'
import Search from '../algolia/components/Search'

function getCategoryId () {
  if (typeof window === 'undefined') return null
  return window.inSidedData.content.category.id
}

function isTopicNew () {
  if (typeof window === 'undefined') return null
  return window.location.pathname.match(/^\/topic\/new/)
}

export interface SearchInfoProps {
  isFederatedSalesforceSearch: boolean
  isFederatedSkilljarSearch: boolean
  isFederatedFreshdeskSearch: boolean
  category?: number
  groupId?: number
  isParentCategory?: boolean
  searchByContentType?: string[]
}

interface Props {
  items: WidgetSetting[]
  groupCount: number
  communityCategories: ParentCategoryInfo[]
  knowledgeBaseCategories: ParentCategoryInfo[]
  logo: string
  ssoLoginUrl: string
  communityCustomerTitle: string
  displaySearchInWidget: boolean
  displayFilters?: boolean
  searchInfo: SearchInfoProps
  permissions: {
    ideation: boolean
    productUpdates: boolean
  }
}

interface State {
  extendSearch: boolean
  enableFocusToSearch: boolean
  items: WidgetSetting[]
}

class HeaderNavigation extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      extendSearch: false,
      enableFocusToSearch: false,
      items: props.items.filter(item => item.visibility).filter(item => this.hasPermissions(item.key))
    }

    this.checkSliderStatus = this.checkSliderStatus.bind(this)
    this.hasPermissions = this.hasPermissions.bind(this)
  }

  checkSliderStatus (newValue: boolean) {
    const bodyWrapper = window.document.body
    if (newValue) {
      bodyWrapper.classList.add('body-overlay')
    } else {
      bodyWrapper.classList.remove('body-overlay')
    }
  }

  hasPermissions (item: string) {
    if (item === 'productUpdates' || item === 'ideation') {
      return this.props.permissions[item]
    }
    return true
  }

  render ({ ssoLoginUrl, communityCustomerTitle, logo, displaySearchInWidget, searchInfo, displayFilters, knowledgeBaseCategories, communityCategories }: Props,
    { extendSearch, enableFocusToSearch, items }: State) {
    return (
      <CurrentUserContext.Consumer>
        {(user) => (
          <ModulesContext.Consumer>
            {modules => (
              <section className='main-navigation--wrapper header-navigation'>
                <div className='main-navigation-sitewidth'>
                  {logo && (
                    <div className='header-navigation_logo-wrapper'>
                      <TrackingLink
                        href='/' target='_self'
                        className='header-navigation_logo-anchor'
                        track={{
                          type: 'Mega Menu click on logo',
                          event_source: 'control'
                        }}
                      >
                        <img
                          className='header-navigation_logo' src={logo} title=''
                          alt={`${communityCustomerTitle} Logo`}
                        />
                      </TrackingLink>
                    </div>
                  )}
                  <div className='header-navigation-items-wrapper'>
                    <div className={classNames('header-navigation-items_and_search', {
                      'search-extended': extendSearch
                    })}
                    >
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <nav role='navigation'>
                          <MegaMenuLinks
                            openOnHover
                            className='header-navigation-items_menu'
                            items={items}
                            user={user}
                            modules={modules}
                            knowledgeBaseCategories={knowledgeBaseCategories}
                            communityCategories={communityCategories}
                          />
                        </nav>
                        <div className='header-navigation-items_hamburger'>
                          <Slider
                            title='Menu'
                            onCallback={(newValue) => this.checkSliderStatus(newValue)}
                            trigger={<Icon icon='menu' />}
                          >
                            <MegaMenuLinks
                              openOnHover={false}
                              className='header-navigation-items_hamburger-list'
                              items={items}
                              user={user}
                              modules={modules}
                              knowledgeBaseCategories={knowledgeBaseCategories}
                              communityCategories={communityCategories}
                            />
                          </Slider>
                        </div>
                        {
                            logo && (
                              <div className='header-navigation_logo-wrapper is-hidden-L'>
                                <TrackingLink
                                  target='_self'
                                  href='/'
                                  className='header-navigation_logo-anchor'
                                  track={{
                                    type: 'Mega Menu click on logo',
                                    event_source: 'control'
                                  }}
                                >
                                  <img className='header-navigation_logo' src={logo} title='' alt='Logo' />
                                </TrackingLink>
                              </div>
                            )
                          }
                      </div>
                      {displaySearchInWidget && (
                        <MegaMenuSearch
                          searchInfo={searchInfo}
                          isUserGuest={!!user.id}
                          extendSearch={extendSearch}
                          enableFocusToSearch={enableFocusToSearch}
                          displayFilters={displayFilters}
                          enableExtendedSearch={() => this.setState({ extendSearch: true, enableFocusToSearch: true })}
                          disableExtendedSearch={() => this.setState({ extendSearch: false, enableFocusToSearch: false })}
                        />
                      )}
                    </div>
                  </div>

                  <section className='main-navigation--nav-buttons-wrapper' data-view='MainNavigation'>
                    <ul>
                      {!isTopicNew() && (
                        <li className='is-hidden-S'>
                          <Phrase p='main.navigation.add_topic'>
                            {(title) =>
                              <a
                                href={getCategoryId() ? `/topic/new?fid=${getCategoryId()}` : '/topic/new'}
                                data-track={JSON.stringify({ trigger: 'navigation', type: 'Topic Initiated' })}
                                data-ga-track={JSON.stringify({
                                  eventCategory: 'Homepage',
                                  eventAction: 'Create topic clicked',
                                  eventLabel: { Position: 'Navigation' }
                                })}
                                className='menu-create-topic qa-menu-create-topic btn btn--cta'
                                role='button'
                                title={title}
                              >
                                <span aria-hidden='true' className='header-navigation-button-icon'><Icon icon='add' width={16} height={16} color='currentColor' /></span>
                                <span>{title}</span>
                              </a>}
                          </Phrase>
                        </li>
                      )}

                      <Phrase m='Common' p='main.navigation.login'>
                        {(loginPhrase) => !user.id
                          ? (
                            <a
                              role='button' href={ssoLoginUrl || '#'}
                              // className='header-login-button qa-header-login-button btn btn--secondary'
                              className='header-login-button qa-header-login-button btn btn--secondary'
                              onClick={e => {
                                if (ssoLoginUrl) return
                                e.preventDefault()
                                window.app.events.commands.execute('modal:login:show')
                              }}
                            >
                              <span aria-hidden='true' className='header-navigation-button-icon'><Icon icon='person' color='currentColor' /></span>
                              <span>{loginPhrase}</span>
                            </a>
                            )
                          : <Dropdown {...user} size='M' />}
                      </Phrase>
                    </ul>
                  </section>
                </div>
                {
                    displaySearchInWidget && (
                      <div className='header-navigation-search_mobile'>
                        <div className='header-navigation_extendable-search extended is-visible-S'>
                          <div className='header-navigation_extended-search'>
                            <form method='get' action='/search/index'>
                              <div>
                                <Search
                                  {...searchInfo}
                                  isUserGuest={!!user.id}
                                  enableFocusToSearch={enableFocusToSearch}
                                  callbackOnOutsideClick={() => this.setState({ extendSearch: false, enableFocusToSearch: false })}
                                />
                              </div>
                            </form>
                          </div>
                        </div>
                      </div>
                    )
                  }
              </section>
            )}</ModulesContext.Consumer>
        )}
      </CurrentUserContext.Consumer>
    )
  }
}

export default HeaderNavigation
